import { Point, Geometry } from 'geojson'
import { LatLngLiteral } from 'leaflet'
import { DatasetType } from '../../../../shared/components/DataSelection/types'
import { fetchWithToken } from '../../../../shared/utils/api/api'
import buildAddressString from '../../../OneDesignDetailPages/AddressDetail/buildAddressString'
import AuthScope from '../../../../shared/utils/api/authScope'
import DATA_SELECTION_CONFIG from '../../../../shared/components/DataSelection/utils/dataSelectionConfig'

export const POLYLINE_BUTTON_TITLE = 'Teken een lijn om de afstand te meten'
export const POLYGON_BUTTON_TITLE = 'Teken een vlak om data te selecteren'
export const REMOVE_BUTTON_TITLE = 'Verwijder geselecteerde tekening'

export interface DrawToolResults {
  results: DrawToolResult[]
  totalPages: number
  totalCount: number
}

interface DrawToolResult {
  identificatie: string
  geometry: Geometry
  name: string
}

type FetchDrawResults = (url: string, signal?: AbortSignal) => Promise<DrawToolResults>

export interface DrawToolConfig {
  authScope: AuthScope
  csvFields: string[]
  endpoint: string
  getDetailPath: (id: string) => string
  fetchResults: FetchDrawResults
  maxNumberOfClusters: number
  clusterFields: string[]
  listFields: string[]
  polygonKey: string
  title: string
}

interface BagResponse {
  _embedded: {
    adresseerbareobjecten: Array<{
      adresseerbaarObjectPuntGeometrie: Point
      identificatie: string
      openbareruimteNaam?: string
      huisnummer?: number
      huisletter?: string
      huisnummertoevoeging?: string
    }>
  }
  page?: {
    totalElements?: number
    totalPages?: number
  }
}

interface BrkResponse {
  _embedded: {
    brkkadastraleobjecten: Array<{
      identificatie: string
      kadastraleAanduiding?: string
      geometrie: Geometry
    }>
  }
  page?: {
    totalElements?: number
    totalPages?: number
  }
}

interface HrResponse {
  _embedded: {
    handelsregister: Array<{
      vestigingsnummer: string
      geometrieRd: Geometry
      eersteHandelsnaam?: string
    }>
  }
  page?: {
    totalElements?: number
    totalPages?: number
  }
}

export const polygonToString = (polygon?: LatLngLiteral[]) =>
  `POLYGON((${polygon?.map(({ lat, lng }) => [lng, lat].join(' ')).join(', ')}, ${polygon?.[0]
    ?.lng} ${polygon?.[0]?.lat}))`

const fetchBagResults: FetchDrawResults = (url, signal) => {
  return fetchWithToken<BagResponse>(url, undefined, { signal }).then((response) => {
    return {
      results: response._embedded.adresseerbareobjecten.map((obj) => ({
        identificatie: obj.identificatie,
        geometry: obj.adresseerbaarObjectPuntGeometrie,
        name: buildAddressString({
          openbareruimteNaam: obj.openbareruimteNaam ?? '',
          huisnummer: obj.huisnummer ?? 0,
          huisletter: obj.huisletter ?? undefined,
          huisnummertoevoeging: obj.huisnummertoevoeging ?? undefined,
        }),
      })),
      totalPages: response.page?.totalPages ?? 1,
      totalCount: response.page?.totalElements ?? 0,
    }
  })
}

const fetchBrkResults: FetchDrawResults = (url, signal) => {
  return fetchWithToken<BrkResponse>(url, undefined, { signal }).then((response) => {
    return {
      results: response._embedded.brkkadastraleobjecten.map((obj) => ({
        identificatie: obj.identificatie,
        geometry: obj.geometrie,
        name: obj.kadastraleAanduiding ?? obj.identificatie,
      })),
      totalPages: response.page?.totalPages ?? 1,
      totalCount: response.page?.totalElements ?? 0,
    }
  })
}

const fetchHrResults: FetchDrawResults = (url, signal) => {
  return fetchWithToken<HrResponse>(url, undefined, { signal }).then((response) => {
    return {
      results: response._embedded.handelsregister.map((obj) => ({
        identificatie: obj.vestigingsnummer,
        geometry: obj.geometrieRd,
        name: obj.eersteHandelsnaam ?? obj.vestigingsnummer,
      })),
      totalPages: response.page?.totalPages ?? 1,
      totalCount: response.page?.totalElements ?? 0,
    }
  })
}

const DRAW_TOOL_CONFIG: Record<DatasetType, DrawToolConfig> = {
  [DatasetType.Bag]: {
    authScope: AuthScope.None,
    endpoint: 'v1/benkagg/adresseerbareobjecten/',
    getDetailPath: (id) => `/adressen/${id}`,
    fetchResults: fetchBagResults,
    csvFields: DATA_SELECTION_CONFIG[DatasetType.Bag].downloadFields,
    clusterFields: ['identificatie', 'adresseerbaarObjectPuntGeometrie'],
    listFields: [
      'identificatie',
      'openbareruimteNaam',
      'huisnummer',
      'huisletter',
      'huisnummertoevoeging',
      'adresseerbaarObjectPuntGeometrie',
    ],
    maxNumberOfClusters: 10000,
    polygonKey: 'adresseerbaarObjectPuntGeometrieWgs84',
    title: 'Adressen',
  },
  [DatasetType.Brk]: {
    authScope: AuthScope.None,
    csvFields: DATA_SELECTION_CONFIG[DatasetType.Brk].downloadFields,
    endpoint: 'v1/benkagg/brkkadastraleobjecten/',
    getDetailPath: (id) => `/kadastrale-objecten/${id}`,
    fetchResults: fetchBrkResults,
    clusterFields: ['identificatie', 'geometrie'],
    listFields: ['identificatie', 'kadastraleAanduiding', 'geometrie'],
    maxNumberOfClusters: 10000,
    polygonKey: 'geometrie',
    title: 'Kadastrale objecten',
  },
  [DatasetType.Hr]: {
    authScope: AuthScope.HrR,
    csvFields: DATA_SELECTION_CONFIG[DatasetType.Hr].downloadFields,
    endpoint: 'v1/benkagg/handelsregister/',
    getDetailPath: (id) => `/vestigingen/${id}`,
    fetchResults: fetchHrResults,
    clusterFields: ['vestigingsnummer', 'geometrieRd'],
    listFields: ['vestigingsnummer', 'geometrieRd', 'eersteHandelsnaam'],
    maxNumberOfClusters: 10000,
    polygonKey: 'geometrieRd',
    title: 'Vestigingen',
  },
}

export const getDrawToolConfig = (type: DatasetType) => DRAW_TOOL_CONFIG[type]
